import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../css/main.scss";

const testimonials = [
  {
    title: "A beautiful everyday notebook",
    quote:
      "The paper feels substantial, the binding opens comfortably, and the restrained design fits naturally on my desk.",
    author: "Julie S.",
  },
  {
    title: "Thoughtful from end to end",
    quote:
      "Every detail feels considered, from the practical storage to the protective packaging and calm color palette.",
    author: "Sonia C.",
  },
  {
    title: "Creative tools I actually use",
    quote:
      "The collection makes it easy to keep my writing tools organized and gives me a reason to return to paper every day.",
    author: "Carol E.",
  },
];

const blogPosts = [
  {
    id: 1,
    image: "/main/ioomparade/journal-guide.png",
    date: "September 18, 2026",
    dateTime: "2026-09-18",
    title: "A Simple Daily Journaling Practice",
    excerpt:
      "A practical approach to choosing a time, a page format, and a prompt that makes writing feel natural.",
  },
  {
    id: 2,
    image: "/main/ioomparade/desk-guide.png",
    date: "September 4, 2026",
    dateTime: "2026-09-04",
    title: "Build a Calm Writing Desk",
    excerpt:
      "Use trays, cases, and a few purposeful tools to create a workspace that stays tidy without feeling empty.",
  },
  {
    id: 3,
    image: "/main/ioomparade/paper-guide.png",
    date: "August 22, 2026",
    dateTime: "2026-08-22",
    title: "Choosing Paper for Pens and Paint",
    excerpt:
      "Compare paper weight, surface texture, and binding styles before choosing a journal for your favorite medium.",
  },
];

function Main() {
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const activeTestimonial = testimonials[testimonialIndex];

  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const showPreviousTestimonial = () => {
    setTestimonialIndex((index) =>
      index === 0 ? testimonials.length - 1 : index - 1
    );
  };

  const showNextTestimonial = () => {
    setTestimonialIndex((index) => (index + 1) % testimonials.length);
  };

  return (
    <main className="main-page">
      <section className="home-hero" aria-labelledby="home-hero-title">
        <img
          className="home-hero-image"
          src="/main/ioomparade/hero.png"
          alt=""
          width="2048"
          height="1536"
          loading="eager"
        />
        <div className="home-hero-shade" aria-hidden="true" />
        <div className="home-hero-content">
          <h1 id="home-hero-title">Make room for your next idea.</h1>
          <Link className="ink-button" to="/shop" onClick={toTop}>
            Shop New Releases
          </Link>
        </div>
        <div className="home-hero-caption" aria-hidden="true">
          New ioomparade Items
        </div>
      </section>

      <section className="home-feature" aria-labelledby="home-feature-title">
        <div className="home-feature-visual">
          <img
            src="/main/ioomparade/feature.png"
            alt="A curated ioomparade writing collection"
            width="360"
            height="360"
            loading="lazy"
          />
        </div>
        <div className="home-feature-copy">
          <h2 id="home-feature-title">Tools for Everyday Creativity</h2>
          <p>
            ioomparade brings together practical notebooks, writing tools, and
            desktop objects designed to make creative routines easier to begin
            and more enjoyable to keep.
          </p>
          <Link
            className="ink-button"
            to="/shop?search=writing"
            onClick={toTop}
          >
            Shop Writing Tools
          </Link>
        </div>
      </section>

      <section
        className="home-testimonials"
        aria-label="ioomparade customer stories"
      >
        <div className="testimonial-stage" aria-live="polite">
          <figure
            className="testimonial-slide"
            role="group"
            aria-roledescription="slide"
            aria-label={`${testimonialIndex + 1} of ${testimonials.length}`}
          >
            <h2>{activeTestimonial.title}</h2>
            <blockquote>
              <p>{activeTestimonial.quote}</p>
            </blockquote>
            <figcaption>{activeTestimonial.author}</figcaption>
          </figure>
        </div>

        <div className="testimonial-controls">
          <button
            type="button"
            className="testimonial-arrow"
            aria-label="Previous customer story"
            onClick={showPreviousTestimonial}
          >
            <span aria-hidden="true">←</span>
          </button>
          <div className="testimonial-dots" aria-label="Choose customer story">
            {testimonials.map((testimonial, index) => (
              <button
                type="button"
                key={testimonial.title}
                className={index === testimonialIndex ? "is-active" : ""}
                aria-label={`Show customer story ${index + 1}`}
                aria-current={index === testimonialIndex ? "true" : undefined}
                onClick={() => setTestimonialIndex(index)}
              />
            ))}
          </div>
          <button
            type="button"
            className="testimonial-arrow"
            aria-label="Next customer story"
            onClick={showNextTestimonial}
          >
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </section>

      <section className="home-about" aria-labelledby="home-about-title">
        <img
          className="home-about-image"
          src="/main/ioomparade/about-home.png"
          alt=""
          width="2788"
          height="2261"
          loading="lazy"
        />
        <div className="home-about-shade" aria-hidden="true" />
        <div className="home-about-content">
          <h2 id="home-about-title">About ioomparade</h2>
          <p>
            Based in Hong Kong, ioomparade creates calm, useful stationery for
            writers, artists, students, and anyone who enjoys making ideas
            tangible on paper.
          </p>
          <Link
            className="ink-button ink-button--outline"
            to="/about"
            onClick={toTop}
          >
            Read On
          </Link>
        </div>
      </section>

      <section className="home-blog" aria-labelledby="home-blog-title">
        <div className="home-section-width">
          <h2 id="home-blog-title">The ioomparade Journal</h2>
          <div className="home-blog-grid">
            {blogPosts.map((post) => (
              <article className="home-blog-card" key={post.id}>
                <Link to="/shop" onClick={toTop}>
                  <div className="home-blog-image">
                    <img
                      src={post.image}
                      alt=""
                      width="300"
                      height={post.id === 2 ? "225" : "300"}
                      loading="lazy"
                    />
                  </div>
                  <div className="home-blog-copy">
                    <time dateTime={post.dateTime}>{post.date}</time>
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default Main;
